import React from 'react';

export interface GoogleBrandIconProps {
  /** numeric px value, used for both height and width */
  size?: number;
  /** Hidden from assistive tech by default; the button supplies the accessible name. */
  'aria-hidden'?: boolean;
}

/**
 * Google "G" logo in official brand colors. Inlined here (rather than pulled
 * from bricks-next) so the redirect button family carries no bricks-next
 * dependency. Google's brand guidance requires the multicolor mark on the
 * "Continue with Google" button, so we keep the SVG instead of a monochrome
 * icon-set glyph.
 */
const luminanceMask: React.CSSProperties = { maskType: 'luminance' };

export const GoogleBrandIcon: React.FC<GoogleBrandIconProps> = ({
  size = 24,
  'aria-hidden': ariaHidden = true,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="80.5 19.5 21 21"
    width={size}
    height={size}
    fill="none"
    aria-hidden={ariaHidden}
  >
    <g clipPath="url(#clip0_397_449)">
      <mask
        id="mask0_397_449"
        style={luminanceMask}
        maskUnits="userSpaceOnUse"
        x="80"
        y="19"
        width="22"
        height="22"
      >
        <path
          d="M101.298 28.052H91.2237V32.0889H97.0126C96.9196 32.6602 96.7106 33.2223 96.4046 33.7347C96.054 34.3219 95.6206 34.7689 95.1763 35.1093C93.8456 36.1291 92.2941 36.3376 91.2166 36.3376C88.4948 36.3376 86.1692 34.5784 85.2688 32.188C85.2325 32.1013 85.2084 32.0117 85.179 31.9231C84.9801 31.3147 84.8714 30.6704 84.8714 30.0007C84.8714 29.3037 84.9891 28.6365 85.2037 28.0064C86.0503 25.5212 88.4284 23.6651 91.2186 23.6651C91.7798 23.6651 92.3203 23.7319 92.8328 23.8651C94.0041 24.1696 94.8326 24.7694 95.3403 25.2437L98.4036 22.2438C96.5402 20.5353 94.1111 19.5 91.2135 19.5C88.8971 19.5 86.7584 20.2217 85.0059 21.4414C83.5846 22.4306 82.419 23.755 81.6324 25.2932C80.9007 26.7193 80.5 28.2998 80.5 29.9991C80.5 31.6985 80.9013 33.2954 81.633 34.7083V34.7179C82.4058 36.2179 83.536 37.5095 84.9097 38.4942C86.1097 39.3544 88.2614 40.5 91.2135 40.5C92.9112 40.5 94.4158 40.1939 95.7427 39.6203C96.7 39.2065 97.5481 38.6668 98.316 37.9732C99.3306 37.0566 100.125 35.923 100.668 34.6187C101.21 33.3144 101.5 31.8394 101.5 30.2403C101.5 29.4956 101.425 28.7393 101.298 28.0519V28.052Z"
          fill="white"
        />
      </mask>
      <g mask="url(#mask0_397_449)">
        <g filter="url(#filter0_f_397_449)">
          <path
            d="M80.3456 30.0704C80.3567 31.7429 80.8333 33.4686 81.5547 34.8617V34.8713C82.0759 35.8829 82.7883 36.6821 83.5997 37.4739L88.5004 35.6858C87.5732 35.2148 87.4317 34.9262 86.7671 34.3996C86.0879 33.7147 85.5817 32.9285 85.2665 32.0066H85.2538L85.2665 31.997C85.0591 31.3882 85.0386 30.742 85.0309 30.0704H80.3456Z"
            fill="url(#paint0_radial_397_449)"
          />
        </g>
        <g filter="url(#filter1_f_397_449)">
          <path
            d="M91.2237 19.4236C90.7394 21.1253 90.9246 22.7794 91.2237 23.7418C91.7831 23.7423 92.3219 23.8089 92.8328 23.9418C94.0041 24.2463 94.8326 24.846 95.3403 25.3204L98.482 22.2438C96.6208 20.5373 94.381 19.4263 91.2237 19.4236Z"
            fill="url(#paint1_radial_397_449)"
          />
        </g>
        <g filter="url(#filter2_f_397_449)">
          <path
            d="M91.2133 19.4102C88.8374 19.4101 86.6439 20.1504 84.8464 21.4014C84.1789 21.866 83.5665 22.4026 83.0211 22.9993C82.8782 24.3397 84.0906 25.9871 86.4916 25.9735C87.6565 24.6184 89.3794 23.7417 91.2969 23.7417C91.2987 23.7417 91.3004 23.7418 91.3021 23.7418L91.2238 19.4105C91.2203 19.4105 91.2169 19.4102 91.2133 19.4102Z"
            fill="url(#paint2_radial_397_449)"
          />
        </g>
        <g filter="url(#filter3_f_397_449)">
          <path
            d="M99.055 30.5554L96.9344 32.0123C96.8413 32.5836 96.6322 33.1456 96.3262 33.6581C95.9756 34.2452 95.5422 34.6923 95.098 35.0327C93.77 36.0503 92.2226 36.2598 91.1454 36.2607C90.032 38.157 89.8368 39.1068 91.2237 40.6373C92.9399 40.6361 94.4613 40.3263 95.8033 39.7462C96.7734 39.3268 97.6328 38.7798 98.411 38.0769C99.4392 37.1481 100.245 35.9992 100.794 34.6774C101.344 33.3556 101.638 31.8609 101.638 30.2404L99.055 30.5554Z"
            fill="url(#paint3_radial_397_449)"
          />
        </g>
        <g filter="url(#filter4_f_397_449)">
          <path
            d="M91.0671 27.8986V32.2423H101.27C101.36 31.6474 101.657 30.8776 101.657 30.2404C101.657 29.4956 101.582 28.586 101.455 27.8986H91.0671Z"
            fill="#3086FF"
          />
        </g>
        <g filter="url(#filter5_f_397_449)">
          <path
            d="M83.0697 22.8459C82.4401 23.5348 81.9022 24.3059 81.4757 25.1398C80.744 26.566 80.3434 28.2998 80.3434 29.9991C80.3434 30.023 80.3454 30.0465 80.3455 30.0704C80.6696 30.6917 84.8216 30.5727 85.0309 30.0704C85.0306 30.0469 85.028 30.0241 85.028 30.0006C85.028 29.3036 85.1457 28.7899 85.3604 28.1598C85.6251 27.3825 86.0397 26.6668 86.5698 26.0501C86.69 25.8967 87.0106 25.5669 87.1041 25.3691C87.1397 25.2937 87.0394 25.2514 87.0338 25.2249C87.0275 25.1952 86.893 25.2191 86.8629 25.197C86.7673 25.1268 86.5778 25.0902 86.4628 25.0576C86.217 24.988 85.8095 24.8345 85.5832 24.6753C84.8678 24.1723 83.7515 23.5715 83.0697 22.8459Z"
            fill="url(#paint4_radial_397_449)"
          />
        </g>
        <g filter="url(#filter6_f_397_449)">
          <path
            d="M85.5985 25.2279C87.2574 26.2327 87.7344 24.7207 88.8373 24.2475L86.9188 20.269C86.2131 20.5656 85.5463 20.9342 84.9276 21.3647C84.0037 22.0078 83.1878 22.7925 82.5138 23.6855L85.5985 25.2279Z"
            fill="url(#paint5_radial_397_449)"
          />
        </g>
        <g filter="url(#filter7_f_397_449)">
          <path
            d="M86.2738 35.3784C84.047 36.1823 83.6984 36.2111 83.4934 37.5911C83.8851 37.9734 84.3059 38.3269 84.7532 38.6475C85.9532 39.5077 88.2615 40.6533 91.2136 40.6533C91.2171 40.6533 91.2204 40.653 91.2238 40.653V36.184C91.2216 36.184 91.219 36.1842 91.2168 36.1842C90.1113 36.1842 89.228 35.8938 88.3222 35.3889C88.0989 35.2644 87.6938 35.5987 87.4878 35.4493C87.2037 35.2431 86.5201 35.6268 86.2738 35.3784Z"
            fill="url(#paint6_radial_397_449)"
          />
        </g>
        <g opacity="0.5" filter="url(#filter8_f_397_449)">
          <path
            d="M89.9197 36.0433V40.5756C90.3327 40.624 90.7626 40.6534 91.2135 40.6534C91.6656 40.6534 92.1029 40.6302 92.5279 40.5875V36.0738C92.0517 36.1552 91.6031 36.1842 91.2167 36.1842C90.7718 36.1842 90.339 36.1324 89.9197 36.0433Z"
            fill="url(#paint7_linear_397_449)"
          />
        </g>
      </g>
    </g>
    <defs>
      <filter
        id="filter0_f_397_449"
        x="79.8755"
        y="29.6003"
        width="9.09494"
        height="8.34369"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter1_f_397_449"
        x="90.4558"
        y="18.9535"
        width="8.49619"
        height="6.83698"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter2_f_397_449"
        x="82.5397"
        y="18.9401"
        width="9.23252"
        height="7.50357"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter3_f_397_449"
        x="89.7764"
        y="29.7703"
        width="12.3315"
        height="11.3371"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter4_f_397_449"
        x="90.5971"
        y="27.4285"
        width="11.5296"
        height="5.28382"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter5_f_397_449"
        x="79.8733"
        y="22.3758"
        width="7.70847"
        height="8.58716"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter6_f_397_449"
        x="79.2089"
        y="16.9642"
        width="12.9332"
        height="11.9063"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="1.65243" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter7_f_397_449"
        x="83.0233"
        y="34.8904"
        width="8.67063"
        height="6.23301"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <filter
        id="filter8_f_397_449"
        x="89.4496"
        y="35.5732"
        width="3.54831"
        height="5.55024"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="0.23504" result="effect1_foregroundBlur_397_449" />
      </filter>
      <radialGradient
        id="paint0_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-0.436381 -10.4579 15.6897 -0.62757 88.4023 37.3163)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.141612" stopColor="#1ABD4D" />
        <stop offset="0.247515" stopColor="#6EC30D" />
        <stop offset="0.311547" stopColor="#8AC502" />
        <stop offset="0.366013" stopColor="#A2C600" />
        <stop offset="0.445673" stopColor="#C8C903" />
        <stop offset="0.540305" stopColor="#EBCB03" />
        <stop offset="0.615636" stopColor="#F7CD07" />
        <stop offset="0.699345" stopColor="#FDCD04" />
        <stop offset="0.771242" stopColor="#FDCE05" />
        <stop offset="0.860566" stopColor="#FFCE0A" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(7.41096 -1.78112e-05 -1.04164e-05 9.3706 98.1881 25.0978)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.408458" stopColor="#FB4E5A" />
        <stop offset="1" stopColor="#FF4540" />
      </radialGradient>
      <radialGradient
        id="paint2_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-10.3833 5.63055 7.80391 13.7952 94.141 18.0537)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.231273" stopColor="#FF4541" />
        <stop offset="0.311547" stopColor="#FF4540" />
        <stop offset="0.457516" stopColor="#FF4640" />
        <stop offset="0.540305" stopColor="#FF473F" />
        <stop offset="0.699346" stopColor="#FF5138" />
        <stop offset="0.771242" stopColor="#FF5B33" />
        <stop offset="0.860566" stopColor="#FF6C29" />
        <stop offset="1" stopColor="#FF8C18" />
      </radialGradient>
      <radialGradient
        id="paint3_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-18.8304 -24.0666 -9.07344 6.80533 91.3781 39.2782)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.131546" stopColor="#0CBA65" />
        <stop offset="0.209784" stopColor="#0BB86D" />
        <stop offset="0.297297" stopColor="#09B479" />
        <stop offset="0.396257" stopColor="#08AD93" />
        <stop offset="0.477124" stopColor="#0AA6A9" />
        <stop offset="0.568425" stopColor="#0D9CC6" />
        <stop offset="0.667385" stopColor="#1893DD" />
        <stop offset="0.768727" stopColor="#258BF1" />
        <stop offset="0.858506" stopColor="#3086FF" />
      </radialGradient>
      <radialGradient
        id="paint4_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-1.33258 11.2456 15.8814 1.80397 90.3036 21.3935)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.366013" stopColor="#FF4E3A" />
        <stop offset="0.457516" stopColor="#FF8A1B" />
        <stop offset="0.540305" stopColor="#FFA312" />
        <stop offset="0.615636" stopColor="#FFB60C" />
        <stop offset="0.771242" stopColor="#FFCD0A" />
        <stop offset="0.860566" stopColor="#FECF0A" />
        <stop offset="0.915033" stopColor="#FECF08" />
        <stop offset="1" stopColor="#FDCD01" />
      </radialGradient>
      <radialGradient
        id="paint5_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-3.85186 4.17093 -12.0157 -10.637 88.4296 21.2769)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.315904" stopColor="#FF4C3C" />
        <stop offset="0.603818" stopColor="#FF692C" />
        <stop offset="0.726837" stopColor="#FF7825" />
        <stop offset="0.884534" stopColor="#FF8D1B" />
        <stop offset="1" stopColor="#FF9F13" />
      </radialGradient>
      <radialGradient
        id="paint6_radial_397_449"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-10.3833 -5.63055 7.80391 -13.7952 94.141 41.946)"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.231273" stopColor="#0FBC5F" />
        <stop offset="0.311547" stopColor="#0FBC5F" />
        <stop offset="0.366013" stopColor="#0FBC5E" />
        <stop offset="0.457516" stopColor="#0FBC5D" />
        <stop offset="0.540305" stopColor="#12BC58" />
        <stop offset="0.699346" stopColor="#28BF3C" />
        <stop offset="0.771242" stopColor="#38C02B" />
        <stop offset="0.860566" stopColor="#52C218" />
        <stop offset="0.915033" stopColor="#67C30F" />
        <stop offset="1" stopColor="#86C504" />
      </radialGradient>
      <linearGradient
        id="paint7_linear_397_449"
        x1="89.9197"
        y1="38.3483"
        x2="92.5279"
        y2="38.3483"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#0FBC5C" />
        <stop offset="1" stopColor="#0CBA65" />
      </linearGradient>
      <clipPath id="clip0_397_449">
        <rect width="21" height="21" fill="white" transform="translate(80.5 19.5)" />
      </clipPath>
    </defs>
  </svg>
);
